import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEffect, useState } from 'react';
import { isDevHubClient } from '../environment/devHub';
import type { SubscriptionMenuProbeMode } from '../probes/subscriptionMenuProbe/types';

export type { SubscriptionMenuProbeMode };

const STORAGE_KEY = '@dev_subscription_menu_probe_mode';

const VALID_MODES: SubscriptionMenuProbeMode[] = ['disabled', 'strict', 'per_day', 'effective_date'];

let cached_mode: SubscriptionMenuProbeMode = 'disabled';

function normalize_mode(raw: string | null | undefined): SubscriptionMenuProbeMode {
  if (raw && VALID_MODES.includes(raw as SubscriptionMenuProbeMode)) {
    return raw as SubscriptionMenuProbeMode;
  }
  return 'disabled';
}

export function getSubscriptionMenuProbeModeSync(): SubscriptionMenuProbeMode {
  if (!isDevHubClient()) {
    return 'disabled';
  }
  return cached_mode;
}

export function isSubscriptionMenuProbeActive(): boolean {
  return getSubscriptionMenuProbeModeSync() !== 'disabled';
}

export async function loadSubscriptionMenuProbePreference(): Promise<SubscriptionMenuProbeMode> {
  if (!isDevHubClient()) {
    cached_mode = 'disabled';
    return 'disabled';
  }
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    cached_mode = normalize_mode(raw);
  } catch {
    cached_mode = 'disabled';
  }
  return cached_mode;
}

export async function setSubscriptionMenuProbeMode(mode: SubscriptionMenuProbeMode): Promise<void> {
  if (!isDevHubClient()) {
    return;
  }
  cached_mode = VALID_MODES.includes(mode) ? mode : 'disabled';
  await AsyncStorage.setItem(STORAGE_KEY, cached_mode);
}

export function useSubscriptionMenuProbeMode(): SubscriptionMenuProbeMode {
  const [mode, set_mode] = useState<SubscriptionMenuProbeMode>(getSubscriptionMenuProbeModeSync);

  useEffect(() => {
    let alive = true;
    loadSubscriptionMenuProbePreference().then(next => {
      if (alive) {
        set_mode(next);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  return mode;
}

export const SUBSCRIPTION_MENU_PROBE_MODE_LABELS: Record<SubscriptionMenuProbeMode, string> = {
  disabled: 'Off (production behavior)',
  strict: 'State 1 — strict activated only',
  per_day: 'State 2 — per-day / any status in range',
  effective_date: 'State 3 — pause effective date',
};
